import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const MarketHeatmap = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const timeframes = [
    { value: '1h', label: '1H' },
    { value: '24h', label: '24H' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'defi', label: 'DeFi' },
    { value: 'layer1', label: 'Layer 1' },
    { value: 'meme', label: 'Meme Coins' },
    { value: 'ai', label: 'AI Tokens' }
  ];

  const heatmapData = [
    { symbol: 'BTC', name: 'Bitcoin', change: 2.34, marketCap: 1.2, size: 'large' },
    { symbol: 'ETH', name: 'Ethereum', change: -1.23, marketCap: 0.4, size: 'large' },
    { symbol: 'BNB', name: 'BNB', change: 4.56, marketCap: 0.08, size: 'medium' },
    { symbol: 'XRP', name: 'XRP', change: -3.21, marketCap: 0.06, size: 'medium' },
    { symbol: 'ADA', name: 'Cardano', change: 1.87, marketCap: 0.04, size: 'medium' },
    { symbol: 'SOL', name: 'Solana', change: 8.92, marketCap: 0.09, size: 'medium' },
    { symbol: 'DOT', name: 'Polkadot', change: -2.45, marketCap: 0.02, size: 'small' },
    { symbol: 'MATIC', name: 'Polygon', change: 5.67, marketCap: 0.015, size: 'small' },
    { symbol: 'AVAX', name: 'Avalanche', change: -4.32, marketCap: 0.018, size: 'small' },
    { symbol: 'LINK', name: 'Chainlink', change: 3.45, marketCap: 0.012, size: 'small' },
    { symbol: 'UNI', name: 'Uniswap', change: 2.78, marketCap: 0.008, size: 'small' },
    { symbol: 'LTC', name: 'Litecoin', change: -1.56, marketCap: 0.007, size: 'small' }
  ];

  const getChangeColor = (change) => {
    if (change > 5) return 'bg-success text-success-foreground';
    if (change > 0) return 'bg-success/70 text-success-foreground';
    if (change > -5) return 'bg-error/70 text-error-foreground';
    return 'bg-error text-error-foreground';
  };

  const getSize = (size) => {
    switch (size) {
      case 'large': return 'col-span-2 row-span-2 h-24';
      case 'medium': return 'col-span-1 row-span-1 h-16';
      default: return 'col-span-1 row-span-1 h-12';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground mb-2">Market Heatmap</h2>
          <p className="text-sm text-muted-foreground">
            Visual representation of cryptocurrency performance by market cap
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 mt-4 lg:mt-0">
          <div className="flex bg-muted rounded-lg p-1">
            {timeframes?.map((timeframe) => (
              <button
                key={timeframe?.value}
                onClick={() => setSelectedTimeframe(timeframe?.value)}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-standard ${
                  selectedTimeframe === timeframe?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {timeframe?.label}
              </button>
            ))}
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {categories?.map((category) => (
              <option key={category?.value} value={category?.value}>
                {category?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Desktop Heatmap */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-6 gap-2 h-96">
          {heatmapData?.map((coin) => (
            <div
              key={coin?.symbol}
              className={`${getSize(coin?.size)} ${getChangeColor(coin?.change)} rounded-lg p-3 flex flex-col justify-between cursor-pointer hover:opacity-80 transition-standard`}
            >
              <div>
                <div className="font-semibold text-sm">{coin?.symbol}</div>
                <div className="text-xs opacity-80 truncate">{coin?.name}</div>
              </div>
              <div className="text-right">
                <div className="font-medium text-sm">
                  {coin?.change > 0 ? '+' : ''}{coin?.change?.toFixed(2)}%
                </div>
                <div className="text-xs opacity-80">
                  ${coin?.marketCap?.toFixed(2)}T
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Mobile Heatmap */}
      <div className="lg:hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {heatmapData?.map((coin) => (
            <div
              key={coin?.symbol}
              className={`${getChangeColor(coin?.change)} rounded-lg p-4 cursor-pointer hover:opacity-80 transition-standard`}
            >
              <div className="font-semibold text-sm mb-1">{coin?.symbol}</div>
              <div className="text-xs opacity-80 mb-2 truncate">{coin?.name}</div>
              <div className="flex justify-between items-end">
                <div className="text-xs opacity-80">
                  ${coin?.marketCap?.toFixed(2)}T
                </div>
                <div className="font-medium text-sm">
                  {coin?.change > 0 ? '+' : ''}{coin?.change?.toFixed(2)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center mt-6 space-x-6 text-xs text-muted-foreground">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success rounded"></div>
          <span>Positive</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-error rounded"></div>
          <span>Negative</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Square" size={12} />
          <span>Size = Market Cap</span>
        </div>
      </div>
    </div>
  );
};

export default MarketHeatmap;